import { Order } from './orders';

/**
 * Felder, die sich bei jedem Speichern zwangsläufig ändern oder rein technischer
 * Natur sind. Sie sagen nichts darüber aus, ob ein Auftrag fachlich verändert wurde,
 * und müssen beim Vergleich ausgeklammert werden.
 */
const VOLATILE_FIELDS = new Set<string>([
  'updated',
  'updatedBy',
  'updatedAt',
  'createdAt',
  'sync',
  // Serverseitige Buchhaltung für die Konflikterkennung — ändert sich bei jedem Save
  // und sagt nichts darüber aus, ob der Nutzer den Auftrag fachlich geändert hat.
  'revision',
]);

/**
 * Vergleicht zwei Order-Stände inhaltlich — unabhängig von Schlüsselreihenfolge und
 * ohne die volatilen Felder. Wird genutzt, um überflüssige Speicherungen und
 * Server-Syncs zu vermeiden: Ohne diese Prüfung erzeugte allein das Öffnen und
 * Verlassen eines Auftrags einen vermeintlichen Änderungsbedarf.
 */
export const hasOrderChanged = (a: Order, b: Order): boolean =>
  stableStringify(a) !== stableStringify(b);

/**
 * Serialisiert einen Wert deterministisch: Objektschlüssel werden sortiert, volatile
 * Felder entfallen. Zwei inhaltlich gleiche Objekte ergeben so immer dieselbe
 * Zeichenkette, auch wenn die Felder in unterschiedlicher Reihenfolge vorliegen.
 */
const stableStringify = (value: unknown): string => {
  if (value === null || typeof value !== 'object') {
    return JSON.stringify(value ?? null);
  }

  if (Array.isArray(value)) {
    return `[${value.map(stableStringify).join(',')}]`;
  }

  const entries = Object.entries(value as Record<string, unknown>)
    .filter(([key]) => !VOLATILE_FIELDS.has(key))
    .sort(([left], [right]) => left.localeCompare(right))
    .map(([key, entryValue]) => `${JSON.stringify(key)}:${stableStringify(entryValue)}`);

  return `{${entries.join(',')}}`;
};
